import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { activities_data } from "../../core/data/json/activities";
import Table from "../../core/common/dataTable/index";
import ActivitiesModal from "../../core/modals/activities_modal";
import { useDispatch, useSelector } from "react-redux";
import {
  setActivityTogglePopup,
  setActivityTogglePopupTwo,
} from "../../core/data/redux/commonSlice";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header";
import { RootState } from "../../core/data/redux/store";
import {
  fetchNotificationsByUserId,
  Notification,
} from "../../api/notification";
import CountUp from "react-countup";
import { useLocation } from "react-router-dom";
import { highlightMatch } from "../../core/common/highlightmatch";
import PageLoadProgressBar from "../../core/data/json/pageLoadProgressBar";
import ScrollToTop from "../../core/common/ScrollToTop";
import { useColumnOrderSave } from "../../core/common/useColumnOrderSave";
import ColumnManagement from "../../core/common/ColumnManagement";

const Activities = () => {
  const route = all_routes;

  const [highlightId, setHighlightId] = useState<string | null>(null);
  const location = useLocation();
     const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const idFromURL = queryParams.get("highlight");

    if (idFromURL) {
      setHighlightId(idFromURL);
      const timer = setTimeout(() => {
        setHighlightId(null);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [location.search]);
  const defaultColumnOrder = [
    "index",
    "message",
    "created_at",
  ];

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
      {
        index: true,
        message: true,
        created_at: true,
      }
    );
    const [orderedData, setOrderedData] = useState<Notification[]>([]);
  const userId = useSelector((state: RootState) => state.auth.user_id);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false); // loading state
    const userRole = useSelector((state: RootState) => state.auth.role);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState<"asc" | "desc" | "recent" | "oldest">(
    "recent"
  );
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
    // Column order state for drag and drop (only non-sticky columns)
    const [columnKeys, setColumnKeys] = useState<string[]>(() => {
      const saved = localStorage.getItem("notificationColumnKeys");
      if (saved) return JSON.parse(saved);
      return defaultColumnOrder;
    });
  
    // Hook for save prompt on navigation/refresh
    const { SaveModal } = useColumnOrderSave({
      columnKeys,
      localStorageKey: "notificationColumnKeys",
      defaultOrder: defaultColumnOrder,
    });

    // Accordion toggle
  const handleAccordionToggle = (key: string) => {
    setOpenAccordion((prev) => (prev === key ? null : key));
  };

   // ✅ Unique lists with useMemo (like your Lead Industry example)
    const uniqueNames = useMemo(
      () =>
        Array.from(
          new Set(notifications.map((d) => d.message?.trim() || ""))
        ).filter((n) => n),
      [notifications]
    );

    const uniqueCreatedAts = useMemo(() => {
    const formattedDates = notifications
      .map((d) => {
        if (!d.created_at) return "";
        const date = new Date(d.created_at);
        if (isNaN(date.getTime())) return "";
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      })
      .filter((n) => n);

    return Array.from(new Set(formattedDates));
  }, [notifications]);

useEffect(() => {
  const getNotifications = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await fetchNotificationsByUserId(userId);
      setNotifications(data);
      setOrderedData(data);
    } catch (err) {
      console.error("Failed to load notifications", err);
    } finally {
      setLoading(false);
    }
  };

  getNotifications();
}, [userId]);

const columnMap: Record<string, any> = {
  index: {
    title: "S.No",
    key: "index",
    width: 80,
    align: "center" as const,
    render: (_: any, __: any, index: number) => (
      <span className="text-muted fw-medium">
        {(currentPage - 1) * pageSize + index + 1}
      </span>
    ),
  },

  message: {
    title: <span className="fw-semibold text-dark">Message</span>,
    dataIndex: "message",
    key: "message",
    sorter: true,
    render: (text: string) => (
      <div
        className="text-muted"
        style={{ whiteSpace: "normal", wordBreak: "break-word" }}
      >
        {highlightMatch(text, searchTerm)}
      </div>
    ),
  },

  created_at: {
    title: <span className="fw-semibold text-dark">Notified At</span>,
    dataIndex: "created_at",
    key: "created_at",
    width: 160,
    align: "center" as const,
    sorter: true,
    render: (value: string) => {
      if (!value) return <span className="text-muted">—</span>;

      const date = new Date(value);
      if (isNaN(date.getTime()))
        return <span className="text-muted">Invalid</span>;

      return (
        <span className="text-muted">
          {highlightMatch(
            `${date.toLocaleDateString("en-IN")} ${date.toLocaleTimeString(
              "en-IN",
              { hour: "2-digit", minute: "2-digit" }
            )}`,
            searchTerm
          )}
        </span>
      );
    },
  },
};

const columns = useMemo(() => {
  return columnKeys
    .filter((key) => visibleColumns[key])
    .map((key) => columnMap[key])
    .filter(Boolean);
}, [
  columnKeys,
  visibleColumns,
  currentPage,
  pageSize,
  searchTerm,
]);


const handleRowOrderChange = (newOrdered: Notification[]) => {
    setOrderedData(newOrdered);
    // OPTIONAL: persist to backend
    // await reorderLeadSources(newOrdered.map(r => r.LeadSource_id));
  };

 const processedNotifications = useMemo(() => {
  const sorted = notifications
    .filter((item) =>
      item.message.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortKey) {
        case "asc":
          return a.message.localeCompare(b.message);
        case "desc":
          return b.message.localeCompare(a.message);
        case "recent":
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case "oldest":
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        default:
          return 0;
      }
    });

  if (highlightId) {
    const index = sorted.findIndex((n) => String(n.id) === String(highlightId));
    if (index !== -1) {
      const [highlighted] = sorted.splice(index, 1);
      return [highlighted, ...sorted];
    }
  }

  return sorted;
}, [notifications, searchTerm, sortKey, highlightId]);

const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  return (
    <>
      <PageLoadProgressBar />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header mb-0">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title"><i className="ti ti-bounce-right text-primary me-2" />
                      Activities
                      <span className="count-title ms-2">
                                              <CountUp
                                                end={notifications.length}
                                                duration={3.0}
                                                separator=","
                                              />
                                            </span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              {/* /Page Header */}
               {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="page-header-breadcrumb mb-3">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link 
                      to={userRole === "admin" ? route.dealsDashboard : route.leadsDashboard}
                      style={{ 
                        color: "#9595b5", 
                        textDecoration: "none",
                        transition: "color 0.2s ease"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = "#E41F07"}
                      onMouseLeave={(e) => e.currentTarget.style.color = "#9595b5"}
                    >
                      Dashboard
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Activities
                  </li>
                </ol>
              </nav>
              {/* /Breadcrumb */}
              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="icon-form mb-3 mb-sm-0 ms-3">
                        <span className="form-icon">
                          <i className="ti ti-search" />
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search Notifications"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                          <button
                            type="button"
                            className="btn-close position-absolute end-0 top-50 translate-middle-y me-3"
                            style={{ zIndex: 10, fontSize: "12px" }}
                            onClick={() => setSearchTerm("")}
                            aria-label="Clear search"
                          />
                        )}
                      </div>
                    </div>
                    {/* <div className="col-sm-8">
                      <div className="text-sm-end">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_add"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add New Activity
                        </Link>
                      </div>
                    </div> */}
                  </div>
                  {/* /Search */}
                </div>

                <div className="card-body">
                  <>
                    {/* Filter */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap mb-4 row-gap-2">
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                        <div className="d-flex align-items-center flex-wrap row-gap-2">
                          <h4 className="mb-0 me-3">All Activity</h4>
                          {/* <div className="active-list">
                            <ul className="mb-0">
                              <li>
                                <Link
                                  to={route.activityCalls}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  data-bs-original-title="Calls"
                                >
                                  <i className="ti ti-phone" />
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to={route.activityMail}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  data-bs-original-title="Mail"
                                >
                                  <i className="ti ti-mail" />
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to={route.activityTask}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  data-bs-original-title="Task"
                                >
                                  <i className="ti ti-subtask" />
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to={route.activityMeeting}
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  data-bs-original-title="Meeting"
                                >
                                  <i className="ti ti-user-share" />
                                </Link>
                              </li>
                            </ul>
                          </div> */}
                          <div className="dropdown me-2">
                          <Link
                            to="#"
                            className="dropdown-toggle dropdown-active-toggle common-sort-dropdown-toggle"
                            data-bs-toggle="dropdown"
                          >
                            <i className="ti ti-sort-ascending-2 me-2" />
                            Sort{" "}
                          </Link>
                          <div className="dropdown-menu  dropdown-menu-start">
                            <ul>
                              <li>
                                <Link
                                  to="#"
                                  className="dropdown-item"
                                  onClick={() => setSortKey("asc")}
                                >
                                  <i className="ti ti-circle-chevron-right me-1" />
                                  Ascending
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="#"
                                  className="dropdown-item"
                                  onClick={() => setSortKey("desc")}
                                >
                                  <i className="ti ti-circle-chevron-right me-1" />
                                  Descending
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="#"
                                  className="dropdown-item"
                                  onClick={() => setSortKey("recent")}
                                >
                                  <i className="ti ti-circle-chevron-right me-1" />
                                  Recently Added
                                </Link>
                              </li>
                              <li>
                                <Link
                                  to="#"
                                  className="dropdown-item"
                                  onClick={() => setSortKey("oldest")}
                                >
                                  <i className="ti ti-circle-chevron-right me-1" />
                                  Oldest
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center flex-wrap row-gap-2">
                        
                        {viewMode === 'list' && (
                      <div className="dropdown me-2 ms-2">
                        <Link
                          to="#"
                          className="btn bg-soft-purple text-purple"
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                        >
                          <i className="ti ti-columns-3 me-2" />
                          Manage Columns
                        </Link>
                        <div
                          className="dropdown-menu dropdown-menu-end dropdown-md p-3"
                          style={{ minWidth: "250px" }}
                        >
                          <h4 className="mb-2 fw-semibold">Manage Columns</h4>
                          <p className="mb-3">
                            Drag and drop columns to reorder and toggle
                            visibility.
                          </p>
                          <div className="border-top pt-3">
                            <ColumnManagement
                              columnKeys={columnKeys}
                              visibleColumns={visibleColumns}
                              onColumnOrderChange={(newOrder) => setColumnKeys(newOrder)}
                              onColumnToggle={handleColumnToggle}
                              fixedKeys={["action"]} // Keeping Action fixed at the end or wherever it is
                            />
                          </div>
                        </div>
                      </div>
                    )}
                      </div>
                    </div>
                    {/* /Filter */}
                  </>

                  {(
  searchTerm.length > 0 ||
  selectedRows.length > 0 ||
  sortKey !== "recent"
) && (
  <div className="active-filters d-flex flex-wrap gap-2 mb-4 align-items-center-3">
    
    {/* Search */}
    {searchTerm.length > 0 && (
      <div className="alert alert-primary py-2 mb-0">
        <strong>Search:</strong> {searchTerm}
        <i
          className="ti ti-x ms-2 cursor-pointer"
          onClick={() => setSearchTerm("")}
        />
      </div>
    )}

    {/* Sort */}
    {sortKey !== "recent" && (
      <div className="alert alert-secondary py-2 mb-0">
        <strong>Sort:</strong>{" "}
        {sortKey
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())}
        <i
          className="ti ti-x ms-2 cursor-pointer"
          onClick={() => setSortKey("recent")}
        />
      </div>
    )}

    {/* Selected rows */}
    {selectedRows.length > 0 && (
      <div className="alert alert-success py-2 mb-0">
        <span>
          <strong>{selectedRows.length}</strong> activity
          {selectedRows.length > 1 ? "ies" : ""} selected
        </span>
        <i
          className="ti ti-x ms-2 cursor-pointer"
          onClick={() => setSelectedRows([])}
        />
      </div>
    )}
  </div>
)}


                  {/* Activity List */}
                  <div className="table-responsive custom-table">
                    <Table
                      columns={columns}
                      dataSource={processedNotifications}
                       loading={loading}
                      rowKey="id"
                      className="custom-table"
                      rowClassName={(record: Notification) =>
                        record.id.toString() === highlightId
                          ? "table-warning blink-highlight"
                          : ""
                      }
                        emptyText="No Activities found"
                        emptyIcon="ti ti-search-off"
                        recordLabel="Activities"
                        enableRowSelection={false}
                        pagination={{
                          current: currentPage,
                          pageSize: pageSize,
                          showSizeChanger: true,
                          pageSizeOptions: ["10", "20", "50"],
                            onChange: (page: number, size: number) => {
                            setCurrentPage(page);
                            setPageSize(size);
                        },
                      }}
                      rowSelection={{
                                                type: "checkbox",
                                                selectedRowKeys: selectedRows,
                                                onChange: (selectedRowKeys: React.Key[]) => {
                                                  setSelectedRows(selectedRowKeys as string[]);
                                                },
                                              }}
                      draggable={true}
                        enableColumnDrag={true}
                        onRowOrderChange={handleRowOrderChange}
                        onColumnOrderChange={(newCols: any[]) => {
                          // Get the new order of non-sticky columns from the dragged columns
                          const newOrderedKeys = newCols
                            .filter((c) => !c.fixed) // Only non-sticky columns
                            .map((c) => c.key || c.dataIndex)
                            .filter(
                              (key) =>
                                key !== undefined && key !== null && key !== ""
                            ); // Remove invalid keys

                          // Preserve columns that are not in newCols (hidden columns) by merging with existing columnKeys
                          const existingKeys = columnKeys;
                          const allNonStickyKeys = defaultColumnOrder; // All possible non-sticky column keys

                          // Create a new array that:
                          // 1. Uses the new order for visible columns
                          // 2. Preserves the order of hidden columns that aren't in newCols
                          const preservedKeys = existingKeys.filter(
                            (key) => !newOrderedKeys.includes(key)
                          );
                          const finalKeys = [
                            ...newOrderedKeys,
                            ...preservedKeys,
                          ];

                          // Ensure all default columns are included (in case some are missing)
                          const missingKeys = allNonStickyKeys.filter(
                            (key) => !finalKeys.includes(key)
                          );
                          const completeKeys = [...finalKeys, ...missingKeys];

                          setColumnKeys(completeKeys);
                          // Don't auto-save, let user decide on navigation/refresh
                        }}
                      scroll={{ x: "max-content" }} // Enables horizontal scroll
                      components={{
                      }}
                    />
                  </div>
                  <div className="row align-items-center">
                    <div className="col-md-6">
                      <div className="datatable-length" />
                    </div>
                    <div className="col-md-6">
                      <div className="datatable-paginate" />
                    </div>
                  </div>
                  {/* /Activity List */}
                </div>
              </div>
            </div>
          </div>
        </div>
        <SaveModal />
        <ActivitiesModal />
      </div>
      {/* /Page Wrapper */}
      <ScrollToTop />
    </>
  );
};

export default Activities;
